import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Area, AreaChart
} from 'recharts';
import { TrendingUp, Users, Activity, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Team colors for charts
const TEAM_COLORS = {
  blue: '#3b82f6',
  green: '#22c55e',
  yellow: '#eab308',
  wound_rn: '#ec4899',
  red: '#ef4444',
  orange: '#f97316',
  purple: '#a855f7'
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-lg border">
        <p className="font-semibold text-sm mb-2">{label}</p>
        {payload.map((entry, idx) => (
          <div key={idx} className="flex items-center gap-2 text-xs">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-foreground">{entry.name}:</span>
            <span className="font-semibold">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export function TeamTrendChart({ token }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ trends: [], teams: [] });
  const [chartType, setChartType] = useState('visits');

  useEffect(() => {
    const fetchTrends = async () => {
      if (!token) return;
      
      try {
        const res = await fetch(`${API_URL}/api/productivity/team-trends`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const result = await res.json();
          setData(result);
        }
      } catch (error) {
        console.error('Error fetching trends:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrends();
  }, [token]);

  if (loading) {
    return <Skeleton className="h-[350px] w-full" />;
  }

  if (!data.trends.length) {
    return (
      <Card>
        <CardContent className="pt-6 text-center text-muted-foreground">
          <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No trend data available</p>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart lines based on teams
  const getTeamKey = (team) => team.toLowerCase().replace(' ', '_');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Team Performance Trends
            </CardTitle>
            <CardDescription>
              Track team metrics over time
            </CardDescription>
          </div>
          <Select value={chartType} onValueChange={setChartType}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="visits">Total Visits</SelectItem>
              <SelectItem value="utilization">Utilization %</SelectItem>
              <SelectItem value="staff">Staff Count</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          {chartType === 'utilization' ? (
            <AreaChart data={data.trends}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="period" 
                tick={{ fontSize: 12 }}
                tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {data.teams.map((team) => (
                <Area
                  key={team}
                  type="monotone"
                  dataKey={`${getTeamKey(team)}_utilization`}
                  name={`${team} %`}
                  stroke={TEAM_COLORS[getTeamKey(team)] || '#6b7280'}
                  fill={TEAM_COLORS[getTeamKey(team)] || '#6b7280'}
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
              ))}
              <Line 
                type="monotone" 
                dataKey="overall_utilization" 
                name="Overall %"
                stroke="#000"
                strokeWidth={2}
                strokeDasharray="5 5"
              />
            </AreaChart>
          ) : (
            <BarChart data={data.trends}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="period" 
                tick={{ fontSize: 12 }}
                tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {data.teams.map((team) => (
                <Bar
                  key={team}
                  dataKey={chartType === 'visits' ? `${getTeamKey(team)}_visits` : `${getTeamKey(team)}_staff`}
                  name={team}
                  fill={TEAM_COLORS[getTeamKey(team)] || '#6b7280'}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
        
        {/* Team Legend with current values */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {data.teams.map((team) => {
            const latestTrend = data.trends[data.trends.length - 1] || {};
            const teamKey = getTeamKey(team);
            const visits = latestTrend[`${teamKey}_visits`] || 0;
            const util = latestTrend[`${teamKey}_utilization`] || 0;
            
            return (
              <div 
                key={team} 
                className="flex items-center gap-2 p-2 rounded-lg border"
                style={{ borderLeftColor: TEAM_COLORS[teamKey], borderLeftWidth: 3 }}
              >
                <div>
                  <p className="font-semibold text-sm">{team}</p>
                  <p className="text-xs text-muted-foreground">
                    {visits} visits • {util}%
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export function ClinicianProductivityTable({ token, period, team }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ records: [], filters: {} });
  const [selectedPeriod, setSelectedPeriod] = useState(period || 'all');
  const [selectedTeam, setSelectedTeam] = useState(team || 'all');

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      setLoading(true);
      try {
        let url = `${API_URL}/api/productivity/clinician-productivity`;
        const params = new URLSearchParams();
        if (selectedPeriod && selectedPeriod !== 'all') params.append('period', selectedPeriod);
        if (selectedTeam && selectedTeam !== 'all') params.append('team', selectedTeam);
        if (params.toString()) url += `?${params.toString()}`;
        
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const result = await res.json();
          setData(result);
        }
      } catch (error) {
        console.error('Error fetching productivity:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, selectedPeriod, selectedTeam]);

  const getOutcomeColor = (outcome) => {
    switch (outcome) {
      case 'Above Target': return 'bg-emerald-100 text-emerald-700';
      case 'On Target': return 'bg-blue-100 text-blue-700';
      case 'Below Target': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getBurnoutColor = (risk) => {
    switch (risk?.toLowerCase()) {
      case 'high': return 'bg-rose-100 text-rose-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      default: return 'bg-emerald-100 text-emerald-700';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Clinician Productivity
            </CardTitle>
            <CardDescription>
              Individual performance metrics
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Periods" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Periods</SelectItem>
                {data.filters?.periods?.map(p => (
                  <SelectItem key={p} value={p}>
                    {new Date(p).toLocaleDateString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedTeam} onValueChange={setSelectedTeam}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="All Teams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {data.filters?.teams?.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : data.records.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No productivity data available</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2">Clinician</th>
                  <th className="text-left py-2 px-2">Team</th>
                  <th className="text-right py-2 px-2">Visits</th>
                  <th className="text-right py-2 px-2">Utilization</th>
                  <th className="text-center py-2 px-2">Outcome</th>
                  <th className="text-center py-2 px-2">Burnout</th>
                </tr>
              </thead>
              <tbody>
                {data.records.map((r, idx) => (
                  <tr key={idx} className="border-b hover:bg-muted/50">
                    <td className="py-2 px-2 font-medium">{r.clinician_name}</td>
                    <td className="py-2 px-2">
                      <Badge variant="outline">{r.team}</Badge>
                    </td>
                    <td className="py-2 px-2 text-right">{r.total_visits}</td>
                    <td className="py-2 px-2 text-right font-semibold">
                      {r.utilization_percent}%
                    </td>
                    <td className="py-2 px-2 text-center">
                      <Badge className={getOutcomeColor(r.outcome)}>
                        {r.outcome}
                      </Badge>
                    </td>
                    <td className="py-2 px-2 text-center">
                      <Badge className={getBurnoutColor(r.burnout_risk)}>
                        {r.burnout_risk}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default TeamTrendChart;
